import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import {
  Tour,
  TourRichContent,
  TourRichContentType,
  TourStepPopoverContent,
} from '@pncl/common-models';

// Turn a tour step into the HTML fragment Driver.js renders inside the popover.
export function buildTourStepHtml(
  popover: TourStepPopoverContent,
  sanitizer: DomSanitizer,
  customClass?: string,
): string {
  const className = ['pencil-tour-card', customClass].filter(Boolean).join(' ');
  const title = popover.title ? `<div class="pencil-tour-heading">${popover.title}</div>` : '';
  const description = `<div class="pencil-tour-content">${buildPopoverDescriptionHtml(
    popover.description,
    sanitizer,
  )}</div>`;
  const copy = `<div class="pencil-tour-copy">${title}${description}</div>`;

  return `
    <div class="${className}">
      ${renderHeroContent(popover.hero)}
      ${copy}
    </div>
  `;
}

// Warm image-based hero assets ahead of time so the popover does not jump on first render.
export function preloadTourRichContent(tour: Tour): Promise<void> {
  const imageHeroes = tour.steps
    .map((step) => step.popoverContent.hero)
    .filter(
      (hero): hero is TourRichContent =>
        !!hero && hero.type === TourRichContentType.IMAGE && hero.url !== '',
    );

  return Promise.all(imageHeroes.map((hero) => preloadImage(hero.url))).then(() => undefined);
}

// The current popover template only supports image heroes; other hero types are skipped.
function renderHeroContent(hero?: TourRichContent): string {
  if (!hero?.url || hero.type !== TourRichContentType.IMAGE) {
    return '';
  }

  return `
    <div class="pencil-tour-hero">
      <img
        class="pencil-tour-hero__asset"
        src="${hero.url}"
        alt="${hero.alt ?? ''}"
        loading="eager"
        decoding="async"
      />
    </div>
  `;
}

// Normalize admin-authored HTML before it is injected so escaped hrefs still behave like links.
function buildPopoverDescriptionHtml(description: string, sanitizer: DomSanitizer): string {
  const container = document.createElement('div');
  const rawHtml = description.replace(/\n/g, '<br />').replace(/\\(["'])/g, '$1');
  const sanitized = sanitizer.sanitize(SecurityContext.HTML, rawHtml) ?? '';

  container.innerHTML = sanitized;
  container.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
    const href = anchor.getAttribute('href');
    if (!href) {
      return;
    }

    anchor.setAttribute('href', href.trim().replace(/^["']+|["']+$/g, ''));
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  return container.innerHTML;
}

// Preloading only needs to wait for completion, not distinguish between load success and failure.
function preloadImage(url: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const image = new Image();
    const finalize = () => {
      image.onload = null;
      image.onerror = null;
      resolve();
    };

    image.onload = finalize;
    image.onerror = finalize;
    image.src = url;

    if (image.complete) {
      finalize();
    }
  });
}
